import { useCallback, useEffect, useRef, useState } from "react";
import type { Socket } from "socket.io-client";
import { apiRequest } from "./api";
import { createEnhancedVoiceTrack, type EnhancedVoiceTrack, voiceAudioConstraints } from "./voiceProcessing";
import { isNativeMobileShell, postNativeMessage, prepareNativeCallAudio, releaseNativeCallAudio } from "./mobileBridge";

export type CallInfo = Readonly<{
  callId: string;
  chatId: string;
  direction: "incoming" | "outgoing";
  peerUserId: string;
  status: "ringing" | "connecting" | "active";
  video: boolean;
}>;

export type CallQuality = Readonly<{
  bitrateKbps: number | null;
  codec: string | null;
  frameRate: number | null;
  latencyMs: number | null;
  level: "checking" | "good" | "fair" | "poor";
  packetLossPercent: number | null;
  resolution: string | null;
  route: "direct" | "turn" | "unknown";
  videoProfile: VideoQualityProfile | null;
}>;

export type VideoQualityProfile = "balanced" | "economy" | "fullhd" | "hd";

type SignalEvent = { callId: string; fromUserId: string; signal: RTCSessionDescriptionInit | RTCIceCandidateInit };
type PendingOffer = { callId: string; fromUserId: string; signal: RTCSessionDescriptionInit };
type PendingIce = { callId: string; candidate: RTCIceCandidateInit };
type CallAck = { error?: { code?: string; message: string }; ok?: true };
type IceServerResponse = { expiresAt?: string; password: string; urls: string[]; username: string };
type NativeCallActionIntent = { actionId: string | null | undefined; callId: string };

function acknowledgeNativeCallAction(actionId?: string | null): void {
  if (actionId) postNativeMessage({ type: "call:action-ack", actionId });
}

const VIDEO_PROFILES: Record<VideoQualityProfile, Readonly<{
  maxBitrate: number;
  maxFramerate: number;
  scaleResolutionDownBy: number;
}>> = {
  fullhd: { maxBitrate: 8_000_000, maxFramerate: 60, scaleResolutionDownBy: 1 },
  hd: { maxBitrate: 4_000_000, maxFramerate: 30, scaleResolutionDownBy: 1.5 },
  balanced: { maxBitrate: 1_800_000, maxFramerate: 24, scaleResolutionDownBy: 2 },
  economy: { maxBitrate: 750_000, maxFramerate: 20, scaleResolutionDownBy: 3 },
};

const EMPTY_QUALITY: CallQuality = {
  bitrateKbps: null,
  codec: null,
  frameRate: null,
  latencyMs: null,
  level: "checking",
  packetLossPercent: null,
  resolution: null,
  route: "unknown",
  videoProfile: null,
};

export function preferredCodecRank(kind: "audio" | "video", mimeType: string): number {
  const normalized = mimeType.toLowerCase();
  if (kind === "audio") {
    if (normalized === "audio/opus") return 0;
    if (normalized === "audio/red") return 1;
    return 10;
  }
  if (normalized === "video/vp9") return 0;
  if (normalized === "video/h264") return 1;
  if (normalized === "video/vp8") return 2;
  if (normalized === "video/av1") return 3;
  return 10;
}

export function selectVideoQualityProfile(input: {
  availableOutgoingKbps: number | null;
  latencyMs: number | null;
  packetLossPercent: number | null;
}): VideoQualityProfile {
  const { availableOutgoingKbps, latencyMs, packetLossPercent } = input;
  if (
    (availableOutgoingKbps !== null && availableOutgoingKbps < 1_000)
    || (latencyMs ?? 0) >= 500
    || (packetLossPercent ?? 0) >= 8
  ) return "economy";
  if (
    (availableOutgoingKbps !== null && availableOutgoingKbps < 2_500)
    || (latencyMs ?? 0) >= 250
    || (packetLossPercent ?? 0) >= 3
  ) return "balanced";
  if (
    (availableOutgoingKbps !== null && availableOutgoingKbps < 7_500)
    || (latencyMs ?? 0) >= 150
    || (packetLossPercent ?? 0) >= 1.5
  ) return "hd";
  return "fullhd";
}

export function tuneOpusSdp(sdp: string): string {
  const newline = sdp.includes("\r\n") ? "\r\n" : "\n";
  const lines = sdp.split(/\r?\n/);
  const opusLine = lines.find((line) => /^a=rtpmap:\d+ opus\/48000(?:\/2)?$/i.test(line));
  const payload = opusLine?.match(/^a=rtpmap:(\d+)/i)?.[1];
  if (!payload) return sdp;
  const fmtpPrefix = `a=fmtp:${payload}`;
  const fmtpIndex = lines.findIndex((line) => line.startsWith(`${fmtpPrefix} `));
  const parameters = new Map<string, string>();
  if (fmtpIndex >= 0) {
    for (const item of lines[fmtpIndex]!.slice(fmtpPrefix.length + 1).split(";")) {
      const [key, value] = item.trim().split("=", 2);
      if (key && value !== undefined) parameters.set(key.toLowerCase(), value);
    }
  }
  for (const [key, value] of [
    ["minptime", "10"],
    ["useinbandfec", "1"],
    ["usedtx", "1"],
    ["maxaveragebitrate", "192000"],
    ["maxplaybackrate", "48000"],
    ["stereo", "0"],
    ["sprop-stereo", "0"],
  ] as const) parameters.set(key, value);
  const tuned = `${fmtpPrefix} ${[...parameters].map(([key, value]) => `${key}=${value}`).join(";")}`;
  if (fmtpIndex >= 0) lines[fmtpIndex] = tuned;
  else lines.splice(lines.indexOf(opusLine) + 1, 0, tuned);
  const result = lines.join(newline);
  return sdp.endsWith(newline) && !result.endsWith(newline) ? `${result}${newline}` : result;
}

function tuneDescription(description: RTCSessionDescriptionInit): RTCSessionDescriptionInit {
  return description.sdp ? { ...description, sdp: tuneOpusSdp(description.sdp) } : description;
}

type IncomingNegotiationPeer = {
  readonly localDescription: RTCSessionDescription | null;
  createAnswer: () => Promise<RTCSessionDescriptionInit>;
  setLocalDescription: (description: RTCSessionDescriptionInit) => Promise<void>;
  setRemoteDescription: (description: RTCSessionDescriptionInit) => Promise<void>;
};

export async function negotiateIncomingMedia(
  peer: IncomingNegotiationPeer,
  offer: RTCSessionDescriptionInit,
  attachLocalTracks: () => Promise<void>,
  applyRemoteIce: () => Promise<void>,
): Promise<RTCSessionDescriptionInit> {
  await peer.setRemoteDescription(offer);
  await attachLocalTracks();
  await applyRemoteIce();
  const answer = tuneDescription(await peer.createAnswer());
  await peer.setLocalDescription(answer);
  return peer.localDescription ?? answer;
}

export function classifyCallQuality(input: {
  bitrateKbps: number | null;
  latencyMs: number | null;
  packetLossPercent: number | null;
  video: boolean;
}): CallQuality["level"] {
  const { bitrateKbps, latencyMs, packetLossPercent, video } = input;
  if (latencyMs === null && packetLossPercent === null && bitrateKbps === null) return "checking";
  if ((latencyMs ?? 0) >= 500 || (packetLossPercent ?? 0) >= 8 || (video && bitrateKbps !== null && bitrateKbps < 180)) return "poor";
  if ((latencyMs ?? 0) >= 250 || (packetLossPercent ?? 0) >= 3 || (video && bitrateKbps !== null && bitrateKbps < 450)) return "fair";
  return "good";
}

function errorName(error: unknown): string {
  return typeof error === "object" && error !== null && "name" in error
    ? String((error as { name: unknown }).name)
    : "";
}

export function mediaErrorMessage(error: unknown, video: boolean): string {
  switch (errorName(error)) {
    case "NotAllowedError":
    case "SecurityError":
      return video
        ? "Нет доступа к камере или микрофону. Разрешите их для live.jtuc.ru в настройках браузера."
        : "Нет доступа к микрофону. Разрешите его для live.jtuc.ru в настройках браузера.";
    case "NotFoundError":
    case "DevicesNotFoundError":
      return video ? "Камера или микрофон не найдены." : "Микрофон не найден.";
    case "NotReadableError":
    case "TrackStartError":
    case "AbortError":
      return video
        ? "Не удалось запустить камеру. Закройте другие приложения и вкладки, использующие камеру, затем повторите попытку."
        : "Не удалось запустить микрофон. Закройте другие приложения, использующие его, затем повторите попытку.";
    case "OverconstrainedError":
      return video ? "Камера не поддерживает запрошенный режим видео." : "Микрофон не поддерживает запрошенный режим.";
    default:
      return error instanceof Error && error.message
        ? error.message
        : video ? "Не удалось начать видеозвонок." : "Не удалось начать аудиозвонок.";
  }
}

const wait = (milliseconds: number): Promise<void> => new Promise((resolve) => {
  globalThis.setTimeout(resolve, milliseconds);
});

export function useCallController(socket: Socket | null) {
  const [call, setCall] = useState<CallInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [muted, setMuted] = useState(false);
  const [cameraEnabled, setCameraEnabled] = useState(true);
  const [quality, setQuality] = useState<CallQuality>(EMPTY_QUALITY);
  const callRef = useRef<CallInfo | null>(null);
  const peerRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const pendingOffer = useRef<PendingOffer | null>(null);
  const pendingIce = useRef<PendingIce[]>([]);
  const answeringOffer = useRef(false);
  const answeredOfferSdp = useRef<string | null>(null);
  const operationInProgress = useRef(false);
  const disconnectedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const statsTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const previousStats = useRef<{ audioBytes: number; lost: number; packets: number; timestamp: number; totalBytes: number; videoBytes: number } | null>(null);
  const videoProfileRef = useRef<VideoQualityProfile>("fullhd");
  const profileCandidateRef = useRef<{ count: number; profile: VideoQualityProfile } | null>(null);
  const iceRestartInProgress = useRef(false);
  const iceCandidateFailures = useRef(0);
  const iceCache = useRef<{ expiresAt: number; servers: RTCIceServer[] } | null>(null);
  const ringtoneContext = useRef<AudioContext | null>(null);
  const voiceProcessorRef = useRef<EnhancedVoiceTrack | null>(null);
  const pendingNativeAnswerRef = useRef<NativeCallActionIntent | null>(null);
  const pendingNativeDeclineRef = useRef<NativeCallActionIntent | null>(null);
  const nativeAnswerInProgressRef = useRef(false);

  const updateCall = useCallback((value: CallInfo | null) => {
    callRef.current = value;
    setCall(value);
  }, []);

  const clearDisconnectedTimer = useCallback(() => {
    if (disconnectedTimer.current !== null) globalThis.clearTimeout(disconnectedTimer.current);
    disconnectedTimer.current = null;
  }, []);

  const cleanup = useCallback(() => {
    const endingCall = callRef.current;
    if (endingCall) postNativeMessage({ type: "call:ended", callId: endingCall.callId });
    releaseNativeCallAudio();
    clearDisconnectedTimer();
    if (statsTimer.current !== null) globalThis.clearInterval(statsTimer.current);
    statsTimer.current = null;
    previousStats.current = null;
    const peer = peerRef.current;
    peerRef.current = null;
    if (peer) {
      peer.onicecandidate = null;
      peer.onicecandidateerror = null;
      peer.ontrack = null;
      peer.onconnectionstatechange = null;
      peer.close();
    }
    localStreamRef.current?.getTracks().forEach((track) => track.stop());
    if (voiceProcessorRef.current) void voiceProcessorRef.current.dispose();
    voiceProcessorRef.current = null;
    remoteStreamRef.current?.getTracks().forEach((track) => track.stop());
    localStreamRef.current = null;
    remoteStreamRef.current = null;
    setLocalStream(null);
    setRemoteStream(null);
    setMuted(false);
    setCameraEnabled(true);
    setQuality(EMPTY_QUALITY);
    pendingOffer.current = null;
    pendingIce.current = [];
    answeringOffer.current = false;
    answeredOfferSdp.current = null;
    videoProfileRef.current = "fullhd";
    profileCandidateRef.current = null;
    iceRestartInProgress.current = false;
    iceCandidateFailures.current = 0;
    operationInProgress.current = false;
    updateCall(null);
  }, [clearDisconnectedTimer, updateCall]);

  const getIceServers = useCallback(async (): Promise<RTCIceServer[]> => {
    if (iceCache.current && iceCache.current.expiresAt > Date.now() + 60_000) return iceCache.current.servers;
    const response = await apiRequest<{ iceServers: IceServerResponse[] }>("/api/rtc/credentials");
    const servers = response.iceServers.map((server) => ({
      urls: server.urls,
      username: server.username,
      credential: server.password,
    }));
    const expiry = Math.min(...response.iceServers.map((server) => server.expiresAt ? Date.parse(server.expiresAt) : Date.now() + 30 * 60_000));
    iceCache.current = { servers, expiresAt: Number.isFinite(expiry) ? expiry : Date.now() + 30 * 60_000 };
    return servers;
  }, []);

  const acquireMedia = useCallback(async (video: boolean): Promise<MediaStream> => {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error("Этот браузер не поддерживает доступ к камере и микрофону.");
    }
    const previous = localStreamRef.current;
    if (previous) {
      previous.getTracks().forEach((track) => track.stop());
      if (voiceProcessorRef.current) await voiceProcessorRef.current.dispose();
      voiceProcessorRef.current = null;
      localStreamRef.current = null;
      setLocalStream(null);
      await wait(200);
    }
    const audio = voiceAudioConstraints();
    try {
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio,
          video: video ? {
            width: { ideal: 1_920, max: 1_920 },
            height: { ideal: 1_080, max: 1_080 },
            frameRate: { ideal: 60, max: 60 },
            aspectRatio: { ideal: 16 / 9 },
            facingMode: { ideal: "user" },
          } : false,
        });
      } catch (caught) {
        const retryable = video && ["AbortError", "NotReadableError", "OverconstrainedError", "TrackStartError"].includes(errorName(caught));
        if (!retryable) throw caught;
        await wait(350);
        stream = await navigator.mediaDevices.getUserMedia({ audio, video: true });
      }
      const rawMicrophone = stream.getAudioTracks()[0];
      if (!rawMicrophone) throw new Error("Микрофон не вернул звуковой поток");
      const voiceProcessor = await createEnhancedVoiceTrack(rawMicrophone);
      voiceProcessorRef.current = voiceProcessor;
      const processed = new MediaStream([voiceProcessor.track, ...stream.getVideoTracks()]);
      processed.getVideoTracks().forEach((track) => { track.contentHint = "motion"; });
      localStreamRef.current = processed;
      setLocalStream(processed);
      return processed;
    } catch (caught) {
      throw new Error(mediaErrorMessage(caught, video), { cause: caught });
    }
  }, []);

  const applyVideoProfile = useCallback(async (peer: RTCPeerConnection, profile: VideoQualityProfile) => {
    const target = VIDEO_PROFILES[profile];
    await Promise.all(peer.getSenders().map(async (sender) => {
      if (sender.track?.kind !== "video") return;
      const parameters = sender.getParameters();
      parameters.degradationPreference = "balanced";
      parameters.encodings = parameters.encodings.length > 0 ? parameters.encodings : [{}];
      parameters.encodings[0] = {
        ...parameters.encodings[0],
        maxBitrate: target.maxBitrate,
        maxFramerate: target.maxFramerate,
        // Audio uses high priority; video yields first when bandwidth contracts.
        networkPriority: "medium",
        priority: "medium",
        scaleResolutionDownBy: target.scaleResolutionDownBy,
      };
      await sender.setParameters(parameters).catch(() => undefined);
    }));
    videoProfileRef.current = profile;
  }, []);

  const optimizeOutgoingMedia = useCallback(async (peer: RTCPeerConnection) => {
    await Promise.all(peer.getSenders().map(async (sender) => {
      const parameters = sender.getParameters();
      parameters.encodings = parameters.encodings.length > 0 ? parameters.encodings : [{}];
      if (sender.track?.kind === "audio") {
        parameters.encodings[0] = {
          ...parameters.encodings[0],
          maxBitrate: 192_000,
          networkPriority: "high",
          priority: "high",
        };
      }
      await sender.setParameters(parameters).catch(() => undefined);
    }));
    await applyVideoProfile(peer, "fullhd");
  }, [applyVideoProfile]);

  const addOptimizedTracks = useCallback(async (peer: RTCPeerConnection, stream: MediaStream) => {
    for (const track of stream.getTracks()) {
      const currentSender = peer.getSenders().find((sender) => sender.track?.kind === track.kind);
      const sender = currentSender ?? peer.addTrack(track, stream);
      if (currentSender && currentSender.track?.id !== track.id) await currentSender.replaceTrack(track);
      const transceiver = peer.getTransceivers().find((item) => item.sender === sender);
      const capabilities = globalThis.RTCRtpSender?.getCapabilities?.(track.kind);
      if (!transceiver || !capabilities?.codecs.length || typeof transceiver.setCodecPreferences !== "function") continue;
      const ranked = capabilities.codecs
        .map((codec, index) => ({ codec, index }))
        .sort((left, right) => (
          preferredCodecRank(track.kind as "audio" | "video", left.codec.mimeType)
          - preferredCodecRank(track.kind as "audio" | "video", right.codec.mimeType)
          || left.index - right.index
        ))
        .map(({ codec }) => codec);
      try {
        transceiver.setCodecPreferences(ranked);
      } catch {
        // The browser keeps its safe default order if it rejects a capability combination.
      }
    }
    await optimizeOutgoingMedia(peer);
  }, [optimizeOutgoingMedia]);

  const drainIce = useCallback(async (peer: RTCPeerConnection, callId: string) => {
    const matching = pendingIce.current.filter((item) => item.callId === callId);
    pendingIce.current = pendingIce.current.filter((item) => item.callId !== callId);
    for (const item of matching) await peer.addIceCandidate(item.candidate);
  }, []);

  const startQualityMonitor = useCallback((peer: RTCPeerConnection, info: CallInfo) => {
    if (statsTimer.current !== null) globalThis.clearInterval(statsTimer.current);
    const inspect = async () => {
      if (peerRef.current !== peer || callRef.current?.callId !== info.callId || peer.connectionState === "closed") return;
      const reports = await peer.getStats();
      let audioBytes = 0;
      let videoBytes = 0;
      let packets = 0;
      let lost = 0;
      let timestamp = Date.now();
      let latencyMs: number | null = null;
      let availableOutgoingKbps: number | null = null;
      let codec: string | null = null;
      let frameRate: number | null = null;
      let remotePacketLossPercent: number | null = null;
      let resolution: string | null = null;
      let route: CallQuality["route"] = "unknown";
      reports.forEach((report) => {
        if (report.type === "inbound-rtp" && !report.isRemote) {
          const reportBytes = Number(report.bytesReceived ?? 0);
          if (report.kind === "video" || report.mediaType === "video") videoBytes += reportBytes;
          else if (report.kind === "audio" || report.mediaType === "audio") audioBytes += reportBytes;
          packets += Number(report.packetsReceived ?? 0);
          lost += Number(report.packetsLost ?? 0);
          timestamp = Math.max(timestamp, Number(report.timestamp ?? timestamp));
          if (report.kind === "video" || report.mediaType === "video") {
            const width = Number(report.frameWidth);
            const height = Number(report.frameHeight);
            const fps = Number(report.framesPerSecond);
            if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) resolution = `${width}×${height}`;
            if (Number.isFinite(fps) && fps > 0) frameRate = Math.round(fps);
          }
          const codecReport = report.codecId ? reports.get(report.codecId) : null;
          if (codecReport?.mimeType && (!codec || report.kind === "video" || report.mediaType === "video")) {
            codec = String(codecReport.mimeType).split("/")[1]?.toUpperCase() ?? String(codecReport.mimeType);
          }
        }
        if (report.type === "candidate-pair" && report.state === "succeeded" && (report.nominated || report.selected)) {
          const rtt = Number(report.currentRoundTripTime);
          if (Number.isFinite(rtt)) latencyMs = Math.round(rtt * 1000);
          const available = Number(report.availableOutgoingBitrate);
          if (Number.isFinite(available) && available > 0) availableOutgoingKbps = Math.round(available / 1000);
          const local = reports.get(report.localCandidateId);
          const remote = reports.get(report.remoteCandidateId);
          route = local?.candidateType === "relay" || remote?.candidateType === "relay" ? "turn" : "direct";
        }
        if (report.type === "remote-inbound-rtp" && (report.kind === "video" || report.mediaType === "video")) {
          const fractionLost = Number(report.fractionLost);
          if (Number.isFinite(fractionLost) && fractionLost >= 0) {
            remotePacketLossPercent = Math.round(fractionLost * 1_000) / 10;
          }
          const remoteRtt = Number(report.roundTripTime);
          if (Number.isFinite(remoteRtt) && remoteRtt >= 0) latencyMs = Math.round(remoteRtt * 1_000);
        }
      });
      const totalBytes = audioBytes + videoBytes;
      const previous = previousStats.current;
      const elapsed = previous ? Math.max(1, timestamp - previous.timestamp) : 0;
      const measuredBytes = info.video ? videoBytes : audioBytes;
      const previousMeasuredBytes = info.video ? previous?.videoBytes : previous?.audioBytes;
      const bitrateKbps = previous && previousMeasuredBytes !== undefined && elapsed > 0
        ? Math.max(0, Math.round(((measuredBytes - previousMeasuredBytes) * 8) / elapsed))
        : null;
      const packetDelta = previous ? Math.max(0, packets - previous.packets) : 0;
      const lostDelta = previous ? Math.max(0, lost - previous.lost) : 0;
      const inboundPacketLossPercent = previous && packetDelta + lostDelta > 0
        ? Math.round((lostDelta / (packetDelta + lostDelta)) * 1000) / 10
        : null;
      // Remote receiver reports describe our upload path; local inbound loss is only a fallback.
      const packetLossPercent = remotePacketLossPercent ?? inboundPacketLossPercent;
      previousStats.current = { audioBytes, packets, lost, timestamp, totalBytes, videoBytes };
      if (info.video) {
        const desiredProfile = selectVideoQualityProfile({ availableOutgoingKbps, latencyMs, packetLossPercent });
        if (desiredProfile === videoProfileRef.current) {
          profileCandidateRef.current = null;
        } else {
          const currentCandidate = profileCandidateRef.current;
          const count = currentCandidate?.profile === desiredProfile ? currentCandidate.count + 1 : 1;
          profileCandidateRef.current = { profile: desiredProfile, count };
          // Downgrade promptly; recover only after several stable samples to avoid oscillation.
          const profileOrder: VideoQualityProfile[] = ["economy", "balanced", "hd", "fullhd"];
          const downgrade = profileOrder.indexOf(desiredProfile) < profileOrder.indexOf(videoProfileRef.current);
          if (count >= (downgrade ? 2 : 4)) {
            await applyVideoProfile(peer, desiredProfile);
            profileCandidateRef.current = null;
          }
        }
      }
      setQuality({
        bitrateKbps,
        codec,
        frameRate,
        latencyMs,
        packetLossPercent,
        resolution,
        route,
        videoProfile: info.video ? videoProfileRef.current : null,
        level: classifyCallQuality({ bitrateKbps, latencyMs, packetLossPercent, video: info.video }),
      });
    };
    void inspect().catch(() => undefined);
    statsTimer.current = globalThis.setInterval(() => { void inspect().catch(() => undefined); }, 2_000);
  }, [applyVideoProfile]);

  const requestIceRestart = useCallback(async (peer: RTCPeerConnection, info: CallInfo) => {
    if (!socket?.connected || iceRestartInProgress.current || peer.signalingState === "closed") return;
    iceRestartInProgress.current = true;
    if (info.direction === "incoming") {
      socket.emit("call:restart-request", { callId: info.callId, targetUserId: info.peerUserId });
      globalThis.setTimeout(() => { iceRestartInProgress.current = false; }, 5_000);
      return;
    }
    try {
      peer.restartIce();
      const offer = tuneDescription(await peer.createOffer({ iceRestart: true }));
      await peer.setLocalDescription(offer);
      socket.emit("call:offer", {
        callId: info.callId,
        targetUserId: info.peerUserId,
        signal: peer.localDescription ?? offer,
      });
      globalThis.setTimeout(() => { iceRestartInProgress.current = false; }, 5_000);
    } catch {
      iceRestartInProgress.current = false;
    }
  }, [socket]);

  const createPeer = useCallback(async (info: CallInfo): Promise<RTCPeerConnection> => {
    if (!socket?.connected) throw new Error("Нет соединения с сервером.");
    const iceServers = await getIceServers();
    const peer = new RTCPeerConnection({
      iceServers,
      bundlePolicy: "max-bundle",
      iceCandidatePoolSize: 8,
      rtcpMuxPolicy: "require",
    });
    peer.onicecandidateerror = (event) => {
      const candidateError = event as RTCPeerConnectionIceErrorEvent;
      // Browsers emit this for every failed STUN/TURN URL and address family.
      // A single IPv6/UDP route can fail while another TURN URL or a direct
      // route connects successfully. Treat it as diagnostics and let the
      // connection-state handler below decide whether the call actually failed.
      if (candidateError.errorCode >= 700) iceCandidateFailures.current += 1;
    };
    peer.onicecandidate = (event) => {
      if (event.candidate && callRef.current?.callId === info.callId) {
        socket.emit("call:ice", {
          callId: info.callId,
          targetUserId: info.peerUserId,
          signal: event.candidate.toJSON(),
        });
      }
    };
    peer.ontrack = (event) => {
      if (peerRef.current !== peer || callRef.current?.callId !== info.callId) return;
      const tracks = new Map<string, MediaStreamTrack>();
      for (const track of remoteStreamRef.current?.getTracks() ?? []) tracks.set(track.id, track);
      for (const track of event.streams[0]?.getTracks() ?? [event.track]) tracks.set(track.id, track);
      // A fresh MediaStream reference makes React rebind the media element when
      // browsers deliver audio and video as separate `track` events.
      const nextStream = new MediaStream([...tracks.values()]);
      remoteStreamRef.current = nextStream;
      setRemoteStream(nextStream);
      const current = callRef.current;
      if (current) updateCall({ ...current, status: "active" });
    };
    peer.onconnectionstatechange = () => {
      if (peerRef.current !== peer || callRef.current?.callId !== info.callId) return;
      if (peer.connectionState === "connected") {
        clearDisconnectedTimer();
        iceRestartInProgress.current = false;
        iceCandidateFailures.current = 0;
        startQualityMonitor(peer, info);
        const current = callRef.current;
        if (current) updateCall({ ...current, status: "active" });
      } else if (peer.connectionState === "disconnected") {
        clearDisconnectedTimer();
        void requestIceRestart(peer, info);
        disconnectedTimer.current = globalThis.setTimeout(() => {
          if (peerRef.current === peer && peer.connectionState === "disconnected") {
            setError("Соединение звонка потеряно. Попробуйте позвонить ещё раз.");
            const current = callRef.current;
            if (current && socket.connected) socket.emit("call:end", { callId: current.callId, state: "ended" });
            cleanup();
          }
        }, 15_000);
      } else if (peer.connectionState === "failed") {
        setError("Сеть изменилась или временно пропала. Восстанавливаем медиасоединение…");
        void requestIceRestart(peer, info);
        clearDisconnectedTimer();
        disconnectedTimer.current = globalThis.setTimeout(() => {
          if (peerRef.current !== peer || peer.connectionState === "connected") return;
          setError(iceCandidateFailures.current > 0
            ? "Не удалось восстановить медиасоединение через доступные прямые и TURN-маршруты. Повторите звонок."
            : "Не удалось восстановить медиасоединение. Проверьте сеть и повторите звонок.");
          if (socket.connected) socket.emit("call:end", { callId: info.callId, state: "ended" });
          cleanup();
        }, 15_000);
      }
    };
    peerRef.current = peer;
    return peer;
  }, [cleanup, clearDisconnectedTimer, getIceServers, requestIceRestart, socket, startQualityMonitor, updateCall]);

  const answerOffer = useCallback(async (peer: RTCPeerConnection, info: CallInfo, offer: PendingOffer) => {
    if (!socket?.connected || offer.callId !== info.callId) return;
    pendingOffer.current = offer;
    if (answeringOffer.current) return;
    answeringOffer.current = true;
    try {
      while (peerRef.current === peer && socket.connected) {
        const nextOffer = pendingOffer.current;
        const stream = localStreamRef.current;
        if (!nextOffer || nextOffer.callId !== info.callId || !stream) break;
        pendingOffer.current = null;
        if (nextOffer.signal.sdp && nextOffer.signal.sdp === answeredOfferSdp.current) continue;

        // Apply the caller's transceivers first, then attach our tracks. This lets
        // the browser reuse the offered audio/video sections and prevents a
        // receive-only answer on the accepting side.
        const answer = await negotiateIncomingMedia(
          peer,
          nextOffer.signal,
          () => addOptimizedTracks(peer, stream),
          () => drainIce(peer, info.callId),
        );
        answeredOfferSdp.current = nextOffer.signal.sdp ?? null;
        socket.emit("call:answer", {
          callId: info.callId,
          targetUserId: info.peerUserId,
          signal: answer,
        });
      }
    } finally {
      answeringOffer.current = false;
    }
  }, [addOptimizedTracks, drainIce, socket]);

  const startCall = useCallback(async (chatId: string, peerUserId: string, video: boolean) => {
    if (!socket?.connected) {
      setError("Нет соединения с сервером.");
      return;
    }
    if (callRef.current || operationInProgress.current) return;
    operationInProgress.current = true;
    setError(null);
    let registeredCallId: string | null = null;
    try {
      await prepareNativeCallAudio(video);
      const stream = await acquireMedia(video);
      if (!socket.connected) throw new Error("Соединение с сервером потеряно.");
      registeredCallId = await new Promise<string>((resolve, reject) => {
        socket.timeout(10_000).emit("call:start", { chatId, targetUserId: peerUserId, video }, (timeout: Error | null, response: { error?: { message: string }; receivedAt?: string }) => {
          if (timeout) return reject(new Error("Сервер не ответил на запрос звонка."));
          if (response.error) return reject(new Error(response.error.message));
          if (!response.receivedAt) return reject(new Error("Сервер не вернул идентификатор звонка."));
          resolve(response.receivedAt);
        });
      });
      const info: CallInfo = {
        callId: registeredCallId,
        chatId,
        peerUserId,
        video,
        direction: "outgoing",
        status: "connecting",
      };
      updateCall(info);
      const peer = await createPeer(info);
      await addOptimizedTracks(peer, stream);
      const offer = tuneDescription(await peer.createOffer());
      await peer.setLocalDescription(offer);
      socket.emit("call:offer", {
        callId: registeredCallId,
        targetUserId: peerUserId,
        signal: peer.localDescription ?? offer,
      });
      operationInProgress.current = false;
    } catch (caught) {
      if (registeredCallId && socket.connected) socket.emit("call:end", { callId: registeredCallId, state: "ended" });
      setError(caught instanceof Error ? caught.message : mediaErrorMessage(caught, video));
      cleanup();
    }
  }, [acquireMedia, addOptimizedTracks, cleanup, createPeer, socket, updateCall]);

  const acceptCall = useCallback(async (): Promise<boolean> => {
    const info = callRef.current;
    if (!info || !socket?.connected || info.direction !== "incoming" || operationInProgress.current) return false;
    operationInProgress.current = true;
    setError(null);
    let acceptedByServer = false;
    try {
      updateCall({ ...info, status: "connecting" });
      // Claim the call before asking Android/WebView for media. A notification
      // Answer must stop the server ringing/re-push loop immediately; media
      // permission/audio setup can legitimately take several seconds after a
      // cold start. Any replayed offer is kept in pendingOffer until the peer
      // and local tracks are ready below.
      await new Promise<void>((resolve, reject) => {
        socket.timeout(10_000).emit("call:accept", { callId: info.callId }, (timeout: Error | null, response: CallAck) => {
          if (timeout) return reject(new Error("Сервер не подтвердил принятие звонка."));
          if (response.error) {
            const acceptedError = new Error(response.error.message) as Error & { code?: string };
            if (response.error.code) acceptedError.code = response.error.code;
            return reject(acceptedError);
          }
          resolve();
        });
      });
      acceptedByServer = true;
      await prepareNativeCallAudio(info.video);
      await acquireMedia(info.video);
      const peer = await createPeer(info);
      const offer = pendingOffer.current;
      if (offer?.callId === info.callId) await answerOffer(peer, info, offer);
      operationInProgress.current = false;
      return true;
    } catch (caught) {
      if ((caught as { code?: string }).code === "CALL_ALREADY_ANSWERED") {
        cleanup();
        return true;
      }
      setError(caught instanceof Error ? caught.message : mediaErrorMessage(caught, info.video));
      if (socket.connected) socket.emit("call:end", { callId: info.callId, state: "declined" });
      cleanup();
      return acceptedByServer;
    }
  }, [acquireMedia, answerOffer, cleanup, createPeer, socket, updateCall]);

  const processPendingNativeAnswer = useCallback(() => {
    const intent = pendingNativeAnswerRef.current;
    const current = callRef.current;
    if (!intent || !current || current.callId !== intent.callId || current.direction !== "incoming" || !socket?.connected) return;
    if (current.status === "active") {
      pendingNativeAnswerRef.current = null;
      acknowledgeNativeCallAction(intent.actionId);
      return;
    }
    if (current.status !== "ringing" || operationInProgress.current || nativeAnswerInProgressRef.current) return;
    nativeAnswerInProgressRef.current = true;
    void acceptCall()
      .then((processed) => {
        if (!processed) return;
        // The same persisted Android action may be replayed while media is
        // opening. Acknowledge the newest copy only after acceptance succeeds.
        const latest = pendingNativeAnswerRef.current;
        if (latest?.callId !== intent.callId) return;
        pendingNativeAnswerRef.current = null;
        acknowledgeNativeCallAction(latest.actionId);
      })
      .finally(() => { nativeAnswerInProgressRef.current = false; });
  }, [acceptCall, socket]);

  const declineNativeCall = useCallback(async (intent: NativeCallActionIntent): Promise<boolean> => {
    if (!socket?.connected) return false;
    return await new Promise<boolean>((resolve) => {
      socket.timeout(10_000).emit("call:end", { callId: intent.callId, state: "declined" }, (timeout: Error | null, response: CallAck) => {
        if (timeout || response?.error) {
          pendingNativeDeclineRef.current = intent;
          resolve(false);
          return;
        }
        pendingNativeDeclineRef.current = null;
        if (callRef.current?.callId === intent.callId) cleanup();
        acknowledgeNativeCallAction(intent.actionId);
        resolve(true);
      });
    });
  }, [cleanup, socket]);

  const endCall = useCallback((state: "ended" | "declined" = "ended") => {
    const current = callRef.current;
    if (current && socket?.connected) socket.emit("call:end", { callId: current.callId, state });
    cleanup();
  }, [cleanup, socket]);

  const toggleMute = useCallback(() => {
    const track = localStreamRef.current?.getAudioTracks()[0];
    if (!track) return;
    track.enabled = !track.enabled;
    setMuted(!track.enabled);
  }, []);

  const toggleCamera = useCallback(() => {
    const track = localStreamRef.current?.getVideoTracks()[0];
    if (!track) return;
    track.enabled = !track.enabled;
    setCameraEnabled(track.enabled);
  }, []);

  useEffect(() => {
    if (!socket) return;
    const incoming = (value: { avatarUrl?: string; callId: string; chatId: string; fromUserId: string; callerName?: string; nativeCallToken?: string; nativeDeclineUrl?: string; startedAt?: string; video: boolean }) => {
      if (callRef.current || operationInProgress.current) {
        return;
      }
      setError(null);
      updateCall({
        callId: value.callId,
        chatId: value.chatId,
        peerUserId: value.fromUserId,
        video: value.video,
        direction: "incoming",
        status: "ringing",
      });
      postNativeMessage({
        type: "call:incoming",
        callId: value.callId,
        chatId: value.chatId,
        fromUserId: value.fromUserId,
        callerName: value.callerName ?? value.fromUserId,
        group: false,
        video: value.video,
        startedAt: value.startedAt,
        avatarUrl: value.avatarUrl,
        nativeCallToken: value.nativeCallToken,
        nativeDeclineUrl: value.nativeDeclineUrl,
      });
    };
    const offer = async (value: SignalEvent) => {
      const current = callRef.current;
      if (!current || current.callId !== value.callId || current.direction !== "incoming") return;
      const nextOffer: PendingOffer = {
        callId: value.callId,
        fromUserId: value.fromUserId,
        signal: value.signal as RTCSessionDescriptionInit,
      };
      pendingOffer.current = nextOffer;
      const peer = peerRef.current;
      if (!peer) return;
      try {
        await answerOffer(peer, current, nextOffer);
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : "Не удалось принять параметры звонка.");
        socket.emit("call:end", { callId: value.callId, state: "ended" });
        cleanup();
      }
    };
    const answer = async (value: SignalEvent) => {
      const peer = peerRef.current;
      const current = callRef.current;
      if (!peer || !current || current.callId !== value.callId || current.direction !== "outgoing") return;
      try {
        await peer.setRemoteDescription(value.signal as RTCSessionDescriptionInit);
        await drainIce(peer, value.callId);
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : "Не удалось принять ответ звонка.");
        socket.emit("call:end", { callId: value.callId, state: "ended" });
        cleanup();
      }
    };
    const restartRequest = (value: { callId: string; fromUserId: string }) => {
      const peer = peerRef.current;
      const current = callRef.current;
      if (!peer || !current || current.callId !== value.callId || current.direction !== "outgoing") return;
      void requestIceRestart(peer, current);
    };
    const ice = (value: SignalEvent) => {
      const current = callRef.current;
      if (!current || current.callId !== value.callId) return;
      const candidate = value.signal as RTCIceCandidateInit;
      const peer = peerRef.current;
      if (!peer || !peer.remoteDescription) {
        pendingIce.current.push({ callId: value.callId, candidate });
        return;
      }
      void peer.addIceCandidate(candidate).catch(() => undefined);
    };
    const ended = (value: { callId: string; state?: string }) => {
      if (callRef.current?.callId !== value.callId) return;
      cleanup();
      if (value.state === "declined") setError("Собеседник отклонил звонок.");
      if (value.state === "missed") setError("На звонок не ответили.");
    };
    const claimed = (value: { callId: string }) => {
      if (callRef.current?.callId === value.callId) cleanup();
    };
    const onOffer = (value: SignalEvent) => { void offer(value); };
    const onAnswer = (value: SignalEvent) => { void answer(value); };
    const disconnected = () => {
      if (!callRef.current) return;
      cleanup();
      setError("Соединение с сервером потеряно. Звонок завершён.");
    };
    socket.on("call:incoming", incoming);
    socket.on("call:offer", onOffer);
    socket.on("call:answer", onAnswer);
    socket.on("call:restart-request", restartRequest);
    socket.on("call:ice", ice);
    socket.on("call:ended", ended);
    socket.on("call:claimed", claimed);
    socket.on("disconnect", disconnected);
    return () => {
      socket.off("call:incoming", incoming);
      socket.off("call:offer", onOffer);
      socket.off("call:answer", onAnswer);
      socket.off("call:restart-request", restartRequest);
      socket.off("call:ice", ice);
      socket.off("call:ended", ended);
      socket.off("call:claimed", claimed);
      socket.off("disconnect", disconnected);
    };
  }, [answerOffer, cleanup, drainIce, requestIceRestart, socket, updateCall]);

  useEffect(() => {
    const incomingFromPush = (event: Event) => {
      const data = (event as CustomEvent<Record<string, string>>).detail ?? {};
      if (data.type !== "incoming_call" || data.group === "true" || !data.callId || !data.chatId || !data.fromUserId) return;
      if (callRef.current || operationInProgress.current) return;
      setError(null);
      updateCall({
        callId: data.callId,
        chatId: data.chatId,
        peerUserId: data.fromUserId,
        video: data.video === "true",
        direction: "incoming",
        status: "ringing",
      });
    };
    window.addEventListener("jtuc:native-push", incomingFromPush as EventListener);
    return () => window.removeEventListener("jtuc:native-push", incomingFromPush as EventListener);
  }, [updateCall]);

  useEffect(() => {
    if (call?.status === "active") postNativeMessage({ type: "call:active", callId: call.callId, video: call.video });
  }, [call]);

  useEffect(() => {
    const answer = (event: Event) => {
      const detail = (event as CustomEvent<{ actionId?: string | null; callId?: string; group?: boolean }>).detail;
      if (detail?.group) return;
      const callId = detail?.callId;
      if (!callId) return;
      const intent = { callId, actionId: detail?.actionId };
      pendingNativeAnswerRef.current = intent;
      processPendingNativeAnswer();
    };
    const end = (event: Event) => {
      const detail = (event as CustomEvent<{ actionId?: string | null; callId?: string; group?: boolean }>).detail;
      if (detail?.group) return;
      const callId = detail?.callId;
      if (!callId) return;
      const intent = { callId, actionId: detail?.actionId };
      if (!socket?.connected) {
        pendingNativeDeclineRef.current = intent;
        if (callRef.current?.callId === callId) cleanup();
        return;
      }
      void declineNativeCall(intent);
    };
    window.addEventListener("jtuc:native-call-answer", answer);
    window.addEventListener("jtuc:native-call-end", end);
    return () => { window.removeEventListener("jtuc:native-call-answer", answer); window.removeEventListener("jtuc:native-call-end", end); };
  }, [cleanup, declineNativeCall, processPendingNativeAnswer, socket]);

  useEffect(() => {
    processPendingNativeAnswer();
  }, [call, processPendingNativeAnswer, socket?.connected]);

  useEffect(() => {
    // A warm WebView can resume between Android's AppState and Chromium's
    // socket/media restoration. Keep retrying the persisted user intent until
    // the controller actually accepts it and sends call:action-ack.
    const timer = window.setInterval(processPendingNativeAnswer, 500);
    const resume = () => processPendingNativeAnswer();
    window.addEventListener("focus", resume);
    document.addEventListener("visibilitychange", resume);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", resume);
      document.removeEventListener("visibilitychange", resume);
    };
  }, [processPendingNativeAnswer]);

  useEffect(() => {
    if (!socket?.connected || !pendingNativeDeclineRef.current) return;
    const pending = pendingNativeDeclineRef.current;
    void declineNativeCall(pending);
  }, [declineNativeCall, socket?.connected]);

  useEffect(() => {
    const primeRingtone = () => {
      try {
        ringtoneContext.current ??= new AudioContext();
        void ringtoneContext.current.resume();
      } catch {
        // Web Audio is optional; browser notifications remain available.
      }
    };
    window.addEventListener("pointerdown", primeRingtone, { passive: true });
    window.addEventListener("keydown", primeRingtone);
    return () => {
      window.removeEventListener("pointerdown", primeRingtone);
      window.removeEventListener("keydown", primeRingtone);
      void ringtoneContext.current?.close();
      ringtoneContext.current = null;
    };
  }, []);

  useEffect(() => {
    const incoming = call?.direction === "incoming" && call.status === "ringing";
    if (!incoming || isNativeMobileShell()) return;
    let timer: ReturnType<typeof setInterval> | null = null;
    let stopped = false;
    const playPulse = () => {
      if (stopped) return;
      try {
        ringtoneContext.current ??= new AudioContext();
      } catch {
        return;
      }
      const context = ringtoneContext.current;
      void context.resume();
      const start = context.currentTime;
      for (const offset of [0, 0.32]) {
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        oscillator.type = "sine";
        oscillator.frequency.setValueAtTime(740, start + offset);
        gain.gain.setValueAtTime(0.0001, start + offset);
        gain.gain.exponentialRampToValueAtTime(0.12, start + offset + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + offset + 0.22);
        oscillator.connect(gain).connect(context.destination);
        oscillator.start(start + offset);
        oscillator.stop(start + offset + 0.24);
      }
    };
    playPulse();
    timer = globalThis.setInterval(playPulse, 2_400);
    if (document.visibilityState === "hidden" && Notification.permission === "granted") {
      new Notification(call.video ? "Входящий видеозвонок" : "Входящий звонок", {
        body: "Откройте JTUC Live, чтобы ответить",
        icon: "/logo.png",
        tag: `call-${call.callId}`,
      });
    }
    return () => {
      stopped = true;
      if (timer !== null) globalThis.clearInterval(timer);
    };
  }, [call]);

  useEffect(() => () => cleanup(), [cleanup]);

  return { acceptCall, call, cameraEnabled, clearError: () => setError(null), endCall, error, localStream, muted, quality, remoteStream, startCall, toggleCamera, toggleMute };
}
